/**
 * Identity Providers Hooks
 *
 * React Query hooks for federated identity provider CRUD operations.
 * The underlying spec defines PUT (not PATCH) for updates -- the verb is
 * preserved here to match the canonical contract.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createIdentityProviderClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';
import { authMiddleware } from '../api/client';

type IdentityProvider = components['schemas']['IdentityProvider'];
type UpdateIdentityProviderRequest = components['schemas']['UpdateIdentityProviderRequest'];

const PROVIDERS_KEY = 'identityProviders';
const PROVIDER_KEY = 'identityProvider';

const client = createIdentityProviderClient();
// Apply the shared platform-admin middleware: CSRF double-submit echo (N8) on
// mutating methods + auth-broken bounce, consistent with every other typed
// client (see api/client.ts). createIdentityProviderClient already defaults
// credentials:'include' (the api-clients factory) so the session cookie rides.
client.use(authMiddleware());

type ApiErrorEnvelope = {
  success?: false;
  error?: { code?: string; message?: string; details?: unknown } | string;
  message?: string;
};

function describeError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  const env = err as ApiErrorEnvelope;
  if (env.message) return env.message;
  if (typeof env.error === 'string') return env.error;
  if (env.error && typeof env.error === 'object' && env.error.message) {
    return env.error.message;
  }
  if (typeof err === 'string') return err;
  return fallback;
}

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    throw new Error(describeError(result.error, `HTTP ${status}: ${statusText}`));
  }
  return result.data;
}

/**
 * Read a single identity provider by ID.
 */
export function useIdentityProvider(providerId: string | undefined) {
  return useQuery({
    queryKey: [PROVIDER_KEY, providerId],
    queryFn: async () => {
      const result = await client.GET('/api/v1/identity-providers/{providerId}', {
        params: { path: { providerId: providerId! } },
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Provider response missing data');
      }
      return wrapped.data as IdentityProvider;
    },
    enabled: !!providerId,
  });
}

/**
 * Update an identity provider configuration via PUT.
 *
 * Spec: PUT /identity-providers/{providerId} -- partial update; only provided
 * fields are persisted. clientSecret is write-only and never returned.
 */
export function useUpdateIdentityProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      providerId,
      body,
    }: {
      providerId: string;
      body: UpdateIdentityProviderRequest;
    }) => {
      const result = await client.PUT('/api/v1/identity-providers/{providerId}', {
        params: { path: { providerId } },
        body,
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Update response missing data');
      }
      return wrapped.data as IdentityProvider;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROVIDER_KEY, variables.providerId] });
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] });
    },
  });
}

/**
 * Suspend an identity provider. Spec: POST /identity-providers/{providerId}/suspend.
 */
export function useSuspendIdentityProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (providerId: string) => {
      const result = await client.POST('/api/v1/identity-providers/{providerId}/suspend', {
        params: { path: { providerId } },
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Suspend response missing data');
      }
      return wrapped.data as IdentityProvider;
    },
    onSuccess: (_data, providerId) => {
      queryClient.invalidateQueries({ queryKey: [PROVIDER_KEY, providerId] });
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] });
    },
  });
}

/**
 * Activate (un-suspend) an identity provider.
 * Spec: POST /identity-providers/{providerId}/activate.
 */
export function useActivateIdentityProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (providerId: string) => {
      const result = await client.POST('/api/v1/identity-providers/{providerId}/activate', {
        params: { path: { providerId } },
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Activate response missing data');
      }
      return wrapped.data as IdentityProvider;
    },
    onSuccess: (_data, providerId) => {
      queryClient.invalidateQueries({ queryKey: [PROVIDER_KEY, providerId] });
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] });
    },
  });
}

/**
 * Decommission an identity provider. Spec: DELETE /identity-providers/{providerId}.
 */
export function useDeleteIdentityProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (providerId: string) => {
      const result = await client.DELETE('/api/v1/identity-providers/{providerId}', {
        params: { path: { providerId } },
      });
      // DELETE may return either the decommissioned record or no body; treat
      // a missing body as success since the spec documents 200 on success.
      if (result.error) {
        const status = result.response?.status ?? 0;
        const statusText = result.response?.statusText ?? 'Unknown';
        throw new Error(describeError(result.error, `HTTP ${status}: ${statusText}`));
      }
      return providerId;
    },
    onSuccess: (providerId) => {
      queryClient.invalidateQueries({ queryKey: [PROVIDER_KEY, providerId] });
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] });
    },
  });
}
